import { ChangeDetectorRef, Directive, OnDestroy, OnInit, inject } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, ValidationErrors, Validators } from '@angular/forms';
import { Subject, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, finalize, switchMap, takeUntil } from 'rxjs/operators';

import { AuthAnalyticsService } from 'ish-core/services/auth-analytics/auth-analytics.service';
import {
  AccessRequestPayload,
  AccessRequestRoute,
  AccessRequestService,
} from 'ish-core/services/access-request/access-request.service';
import { SpecialValidators } from 'ish-shared/forms/validators/special-validators';

export type AccessRequestStep = 'erp' | 'details';

/** Format digits as (XXX) XXX-XXXX while the user types (US-style display). */
export function formatPresidioPhoneInput(raw: string): string {
  const digits = String(raw || '')
    .replace(/\D/g, '')
    .slice(0, 10);
  if (!digits.length) {
    return '';
  }
  if (digits.length <= 3) {
    return `(${digits}`;
  }
  if (digits.length <= 6) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  }
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/** Optional phone: empty OK; otherwise require 10 digits. */
export function optionalPhoneValidator(control: AbstractControl): ValidationErrors | null {
  const digits = String(control.value || '').replace(/\D/g, '');
  if (!digits.length) {
    return null;
  }
  return digits.length === 10 ? null : { phone: { valid: false } };
}

/**
 * Shared request-access logic (not theme-replaced).
 * Theme entry components set `demoErpCustomerNo` for prototype defaults only.
 *
 * Step 1 — verify ERP customer number.
 * Step 2 — collect name / email / password and submit (FR 1.2.7 / 1.4.5).
 */
@Directive()
export abstract class RequestAccessPageBaseComponent implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly accessRequestService = inject(AccessRequestService);
  private readonly authAnalytics = inject(AuthAnalyticsService);
  protected readonly cdRef = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();

  step: AccessRequestStep = 'erp';
  submitted = false;
  submitting = false;
  verifying = false;
  message: string | undefined;
  error: string | undefined;
  routeOutcome: AccessRequestRoute | undefined;
  verifiedCompanyName: string | undefined;
  verifiedErpCustomerNo: string | undefined;
  contactSalesRep = false;
  /** Duplicate email — point user to reset password / contact admin. */
  emailAlreadyExists = false;
  /** Email domain rejected by account approved-domain policy (do not list domains). */
  emailDomainRejected = false;
  /** Normalized ERP that failed verify — disables Verify until the field changes. */
  private failedErpCustomerNo: string | undefined;

  /** Presidio password policy — min length for checklist / help copy. */
  readonly passwordMinLength = 10;

  /** DEMO-ONLY when set by Presidio theme entry. Production default is empty. */
  protected demoErpCustomerNo = '';

  readonly form = this.fb.nonNullable.group(
    {
      erpCustomerNo: ['', [Validators.required, Validators.minLength(1)]],
      firstName: ['', [Validators.required, (c: FormControl) => SpecialValidators.personName(c)]],
      lastName: ['', [Validators.required, (c: FormControl) => SpecialValidators.personName(c)]],
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, (c: FormControl) => SpecialValidators.password(c)]],
      passwordConfirmation: ['', Validators.required],
    },
    {
      validators: [SpecialValidators.equalTo('passwordConfirmation', 'password')],
    }
  );

  /** Disable Verify after a not-found / contact-sales result until ERP text changes. */
  get verifyErpDisabled(): boolean {
    if (this.verifying) {
      return true;
    }
    if (!this.failedErpCustomerNo) {
      return false;
    }
    return this.normalizeErp(this.form.controls.erpCustomerNo.getRawValue()) === this.failedErpCustomerNo;
  }

  ngOnInit(): void {
    if (this.demoErpCustomerNo) {
      this.form.controls.erpCustomerNo.setValue(this.demoErpCustomerNo);
    }
    this.form.controls.erpCustomerNo.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
      if (!this.failedErpCustomerNo) {
        return;
      }
      if (this.normalizeErp(value) !== this.failedErpCustomerNo) {
        this.clearFailedErpVerify();
        this.cdRef.markForCheck();
      }
    });
    // Live password-requirements checklist under OnPush.
    this.form.controls.password.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.cdRef.markForCheck();
    });
    this.form.controls.email.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(rawEmail => {
          const email = String(rawEmail || '').trim();
          // Clear stale domain rejection as the user edits.
          this.clearEmailDomainRejection();
          this.cdRef.markForCheck();

          if (this.step !== 'details' || !this.verifiedErpCustomerNo) {
            return of(null);
          }
          if (!email || this.form.controls.email.hasError('required') || this.form.controls.email.hasError('email')) {
            return of(null);
          }
          // Format-only check: Angular email validator may still be pending mark; use a light shape test.
          if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            return of(null);
          }

          return this.accessRequestService.checkEmailDomain(this.verifiedErpCustomerNo, email).pipe(
            catchError(() => of({ ok: true })), // do not block typing on network errors
            takeUntil(this.destroy$)
          );
        }),
        filter((res): res is { ok: boolean } => !!res)
      )
      .subscribe(res => {
        if (res.ok) {
          this.clearEmailDomainRejection();
        } else {
          this.applyEmailDomainRejection();
        }
        this.cdRef.markForCheck();
      });
  }

  invalid(controlName: keyof typeof this.form.controls): boolean {
    const control = this.form.controls[controlName];
    return !!(control && control.invalid && (control.touched || control.dirty));
  }

  /** Prefer person-name format errors over required when both could apply. */
  nameErrorKey(controlName: 'firstName' | 'lastName'): string {
    const control = this.form.controls[controlName];
    if (control?.hasError('personName')) {
      return 'account.address.name.error.invalid';
    }
    return controlName === 'firstName'
      ? 'account.address.firstname.error.required'
      : 'account.address.lastname.error.required';
  }

  /** Match Formly validation-icons: show check once the control is valid and interacted with. */
  showValid(controlName: keyof typeof this.form.controls): boolean {
    if (controlName === 'erpCustomerNo' && this.step === 'details' && this.verifiedErpCustomerNo) {
      return true;
    }
    const control = this.form.controls[controlName];
    return !!(
      control &&
      control.valid &&
      !!String(control.getRawValue() ?? '').trim() &&
      (control.touched || control.dirty)
    );
  }

  /** Step 1 — validate ERP exists before showing remaining fields. */
  verifyErp(): void {
    const erpControl = this.form.controls.erpCustomerNo;
    erpControl.markAsTouched();
    if (erpControl.invalid || this.verifyErpDisabled) {
      this.focusControl('erpCustomerNo');
      this.cdRef.markForCheck();
      return;
    }

    this.error = undefined;
    this.emailAlreadyExists = false;
    this.clearEmailDomainRejection();
    this.verifying = true;
    this.cdRef.markForCheck();

    const erpCustomerNo = erpControl.value.trim();
    this.accessRequestService
      .verifyErp(erpCustomerNo)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.verifying = false;
          this.cdRef.markForCheck();
        })
      )
      .subscribe({
        next: res => {
          if (res.contactSalesRep) {
            this.markErpVerifyFailed(erpCustomerNo, 'account.request_access.erp.error.contact_sales');
            return;
          }
          this.clearFailedErpVerify();
          this.contactSalesRep = false;
          this.verifiedErpCustomerNo = res.erpCustomerNo || erpCustomerNo;
          this.verifiedCompanyName = res.companyName;
          this.form.controls.erpCustomerNo.setValue(this.verifiedErpCustomerNo);
          this.form.controls.erpCustomerNo.disable({ emitEvent: false });
          this.step = 'details';
          this.cdRef.markForCheck();
          // Move focus into the details step
          setTimeout(() => this.focusControl('firstName'), 0);
        },
        error: (err: { error?: { messageKey?: string }; code?: string }) => {
          const messageKey =
            err?.error?.messageKey ||
            (typeof err?.code === 'string' && err.code.startsWith('account.') ? err.code : undefined) ||
            'account.request_access.erp.error.contact_sales';
          this.markErpVerifyFailed(erpCustomerNo, messageKey);
          this.focusControl('erpCustomerNo');
        },
      });
  }

  /** Return to ERP step so the user can change the verified number. */
  changeErp(): void {
    this.step = 'erp';
    this.verifiedCompanyName = undefined;
    this.verifiedErpCustomerNo = undefined;
    this.emailAlreadyExists = false;
    this.clearEmailDomainRejection();
    this.clearFailedErpVerify();
    this.form.controls.erpCustomerNo.enable({ emitEvent: false });
    this.cdRef.markForCheck();
    setTimeout(() => this.focusControl('erpCustomerNo'), 0);
  }

  private applyEmailDomainRejection(): void {
    this.emailDomainRejected = true;
    this.form.controls.email.setErrors({
      ...(this.form.controls.email.errors || {}),
      emailDomain: true,
    });
    this.form.controls.email.markAsTouched();
  }

  private clearEmailDomainRejection(): void {
    this.emailDomainRejected = false;
    if (this.error === 'account.request_access.error.email_domain') {
      this.error = undefined;
    }
    const emailCtrl = this.form.controls.email;
    if (emailCtrl.hasError('emailDomain')) {
      const errors = { ...(emailCtrl.errors || {}) };
      delete errors.emailDomain;
      emailCtrl.setErrors(Object.keys(errors).length ? errors : null);
    }
  }

  private markErpVerifyFailed(erpCustomerNo: string, messageKey: string): void {
    this.contactSalesRep = true;
    this.error = messageKey;
    this.failedErpCustomerNo = this.normalizeErp(erpCustomerNo);
    this.cdRef.markForCheck();
  }

  private clearFailedErpVerify(): void {
    this.failedErpCustomerNo = undefined;
    this.contactSalesRep = false;
    this.error = undefined;
  }

  private normalizeErp(value: string | null | undefined): string {
    return String(value || '')
      .trim()
      .toUpperCase();
  }

  submit(): void {
    if (this.step !== 'details') {
      this.verifyErp();
      return;
    }

    this.emailAlreadyExists = false;
    this.error = undefined;

    // ERP is disabled after verify — include it via getRawValue()
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.form.updateValueAndValidity({ emitEvent: false });
      this.focusFirstInvalid();
      this.authAnalytics.trackAccessRequestSubmit('validation_error');
      this.cdRef.markForCheck();
      return;
    }

    this.submitting = true;
    this.cdRef.markForCheck();

    const raw = this.form.getRawValue();
    const payload: AccessRequestPayload = {
      erpCustomerNo: (this.verifiedErpCustomerNo || raw.erpCustomerNo).trim(),
      firstName: raw.firstName.trim(),
      lastName: raw.lastName.trim(),
      email: raw.email.trim(),
      password: raw.password,
    };

    this.accessRequestService
      .requestAccess(payload)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.submitting = false;
          this.cdRef.markForCheck();
        })
      )
      .subscribe({
        next: res => {
          this.submitted = true;
          this.routeOutcome = res.route;
          this.message =
            res.messageKey ||
            (res.route === 'sales'
              ? 'account.request_access.success.sales'
              : 'account.request_access.success.admin');
          this.authAnalytics.trackAccessRequestSubmit('success', res.route);
          this.cdRef.markForCheck();
        },
        error: (err: {
          error?: { messageKey?: string; code?: string };
          messageKey?: string;
          code?: string;
          status?: number;
        }) => {
          // HttpErrorResponse body OR ICMErrorMapper HttpError (`code` / top-level messageKey).
          const mappedCode = err?.code || err?.error?.code;
          const messageKey =
            err?.error?.messageKey ||
            err?.messageKey ||
            (typeof mappedCode === 'string' && mappedCode.startsWith('account.') ? mappedCode : undefined) ||
            'account.request_access.error';
          this.error = messageKey;
          this.emailAlreadyExists = messageKey === 'account.request_access.error.email_exists';
          this.emailDomainRejected = messageKey === 'account.request_access.error.email_domain';
          if (this.emailDomainRejected) {
            this.applyEmailDomainRejection();
          }
          this.authAnalytics.trackAccessRequestSubmit('failure');
          this.cdRef.markForCheck();
          if (this.emailAlreadyExists || this.emailDomainRejected) {
            setTimeout(() => this.focusControl('email'), 0);
          }
        },
      });
  }

  private focusFirstInvalid(): void {
    const order: Array<keyof typeof this.form.controls> = [
      'erpCustomerNo',
      'firstName',
      'lastName',
      'email',
      'password',
      'passwordConfirmation',
    ];
    const firstInvalid = order.find(name => this.form.controls[name].invalid);
    if (firstInvalid) {
      this.focusControl(firstInvalid);
    }
  }

  protected focusControl(controlName: keyof typeof this.form.controls): void {
    const el = document.getElementById(controlName);
    el?.focus();
    if (typeof el?.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
